import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { type Unit } from '../../../store/slices/unitsSlice'
import { useAppDispatch, useAppSelector } from '../../../hooks/redux'
import { type RootState } from '../../../store/store'
import { ACCESS_KEY, urlAPI } from '../../../urls'
import { ServicesConfig } from '../../../components/adminModule/clientOrders/servicesConfig'
import { getAdminAllowedStatuses, getLandlordServiceAdminContract, isAdminRelatedServiceEditable, isAdminScheduledStatus } from '../../../components/adminModule/clientOrders/landlordServiceAdminContract'
import { assertSafeRootOrderServiceStatusBody, buildRootOrderServiceStatusUpdateBody, hasLegacyNestedRelatedServices, updateOrderRelatedService, updateRootOrderServiceStatus, type OrderRelatedServicePatch, type RootOrderServiceStatusPatch } from '../../../components/adminModule/clientOrders/orderRelatedServiceUpdates'
import { getConsultationOrdersForUnit, isConsultationOrder, resolveConsultationDisplay } from '../../../components/adminModule/clientOrders/consultationDisplay'
import { sendNotification } from '../../../functions/shared/notifications/sendNotification'
import { toUTCWithOffset } from '../../../functions/agent/date/toUTCWithOffset'
import { getCurrentDubaiDate } from '../../../functions/agent/date/getCurrentDubaiDate'
import {
    type NocPreparedPayload,
    prepareNocSentForSignatureDraft,
    triggerNocSentForSignature
} from '../../../store/actionsCreators/serviceActions/servicesV2/findingTenant/callbacks/triggerNocSentForSignature'
import { resolveRelatedServicesParentOrder } from './findingTenantRelatedServices'
import { findDifcListingNoc, hasPublishEndDate, resolveUnitJurisdiction } from './unitRegulatoryReview'

type ReviewMode = 'landlord' | 'agent'
type ReviewFlowType = 'request' | 'published'

interface IProps {
    currentUnit?: Unit
    mode: ReviewMode
    flowType?: ReviewFlowType
    relatedOrders?: any[]
    onReadyChange: (isReady: boolean) => void
}

interface ViewDoc {
    id: number | string
    label: string
    status: string
    number?: string
    year?: string
    type?: string
    category?: string
    expiry?: string
    url: string
    raw: any
}

interface RelatedServiceView {
    key: string
    serviceId: string
    label: string
    status: string
    additional: string
    isRelated: boolean
    scheduledAt?: string
    notify?: boolean
    documentId?: string
}

interface RelatedOrderRow {
    id: number | string
    order: any
    services: RelatedServiceView[]
    statuses: string
    amount: string
    invoiceStatus: string
    invoiceRef: string
    legacyNestedWarning: boolean
}

interface ResponsibilityChecks {
    reviewedServiceContext: boolean
    confirmedNextStatus: boolean
    acknowledgedLiveImpact: boolean
    acceptedResponsibility: boolean
}

interface NocPreparationState {
    isOpen: boolean
    orderId: number | null
    serviceKey: string | null
    pairKey: string | null
    draft: NocPreparedPayload | null
    loading: boolean
    submitLoading: boolean
    error: string
    fieldErrors: Record<string, string>
}

const buildDefaultResponsibilityChecks = (): ResponsibilityChecks => ({
    reviewedServiceContext: false,
    confirmedNextStatus: false,
    acknowledgedLiveImpact: false,
    acceptedResponsibility: false
})

const TITLE_DEED_KEYS = ['title deed', 'title-deed', 'oqood', 'ownership']
const TENANCY_KEYS = ['ejari', 'tenancy', 'lease']
const PASSPORT_KEYS = ['passport']
const EID_KEYS = ['emirates id', 'emirates-id', 'eid', 'identity']

const SERVICE_ALIASES: Record<string, string> = {
    find_tenant: 'find_tenant',
    finding_tenant: 'find_tenant',
    'finding-tenant': 'find_tenant',
    unit_photography: 'unit_photography',
    'unit-photography': 'unit_photography',
    request_inspection: 'request_inspection',
    'request-inspection': 'request_inspection',
    property_rent_valuation: 'property_rent_valuation',
    noc_generation: 'noc_generation',
    snagging_report: 'snagging_report',
    tenant_screening: 'tenant_screening',
    contract_drafting: 'contract_drafting',
    inventory_checklist: 'inventory_checklist',
    lease_execution: 'lease_execution',
    utility_setup: 'utility_setup',
    53: 'contract_drafting'
}

const SERVICE_LABELS: Record<string, string> = {
    find_tenant: 'Finding a Tenant',
    unit_photography: 'Unit Photography',
    request_inspection: 'Request Inspection',
    property_rent_valuation: 'Property Rent Value Report',
    noc_generation: 'NOC Generation',
    snagging_report: 'Snagging Report',
    tenant_screening: 'Tenant Screening',
    contract_drafting: 'Contract Drafting',
    inventory_checklist: 'Inventory Checklist',
    lease_execution: 'Lease Execution',
    utility_setup: 'Utility Setup'
}

const NOC_RELATED_SERVICE_ID = 'noc_generation'
const NOC_SENT_FOR_SIGNATURE_STATUS = 'Sent for Signature'
const NOC_DRAFT_STORAGE_PREFIX = 'bso-admin-noc-draft'
const REF_NUMBER_REGEXP = /^FAT\d{6}\/U\d{3}$/
const EMAIL_REGEXP = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const toArray = <T,>(value: T[] | null | undefined): T[] => (Array.isArray(value) ? value : [])
const isUnpaidOrder = (order: any): boolean => {
    return toArray<string>(order?.Statuses)
        .some((status) => String(status).trim().toLowerCase() === 'unpaid')
}
const getRootOrderStatus = (order: any): string => String(order?.ServiceStatus ?? toArray<string>(order?.Statuses)[0] ?? '').trim()
const normalizeValue = (value: unknown): string => value == null ? 'Not set' : Array.isArray(value) ? (value.length > 0 ? value.join(', ') : 'Not set') : String(value)
const hasAny = (text: string, words: string[]): boolean => words.some((word) => text.includes(word))
const toDateTimeInputValue = (iso?: string): string => {
    if (iso == null || iso.trim().length === 0) return ''
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return ''
    const pad = (value: number): string => String(value).padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const normalizeServiceToken = (value: unknown): string => String(value ?? '').trim().toLowerCase().replace(/^services\./, '').replace(/[\s-]+/g, '_')
const canonicalServiceId = (value: unknown): string => SERVICE_ALIASES[normalizeServiceToken(value)] ?? normalizeServiceToken(value)
const serviceLabel = (id: string, fallback?: unknown): string => ServicesConfig.getService(canonicalServiceId(id))?.title ?? SERVICE_LABELS[canonicalServiceId(id)] ?? (normalizeServiceToken(fallback) || 'Unknown service').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

const unwrapEntity = (value: any): any => {
    if (value == null) return null
    if (Array.isArray(value?.data)) return value
    return value?.data ?? value
}

const unwrapListDeep = <T,>(value: any): T[] => {
    if (value == null) return []
    if (Array.isArray(value)) return value.flatMap((item) => unwrapListDeep<T>(item))
    if (Array.isArray(value?.data)) return value.data.flatMap((item: any) => unwrapListDeep<T>(item))
    if (value?.data != null) return unwrapListDeep<T>(value.data)
    return [value as T]
}

const toDocId = (value: any): number | null => {
    if (value == null) return null
    if (typeof value === 'number' && Number.isFinite(value)) return value
    if (typeof value === 'string' && /^\d+$/.test(value)) return Number(value)
    const entity = unwrapEntity(value)
    const id = Number(entity?.id ?? entity?.attributes?.id)
    return Number.isFinite(id) && id > 0 ? id : null
}

const getDocTypeParts = (doc: any): any[] => {
    const src = doc?.attributes ?? doc ?? {}
    const raw = src?.Type ?? []
    if (Array.isArray(raw)) return raw
    if (Array.isArray(raw?.data)) return raw.data
    return raw != null ? [raw] : []
}

const getDocLabel = (doc: any): string => {
    const src = doc?.attributes ?? doc ?? {}
    const typePart: any = getDocTypeParts(doc)[0]
    return String(typePart?.Type ?? src?.Category ?? src?.Name ?? src?.Title ?? 'Unknown document')
}

const getDocStatus = (doc: any): string => {
    const src = doc?.attributes ?? doc ?? {}
    return toArray<string>(src?.Statuses)[0] ?? src?.Status ?? 'Unknown'
}

const getDocUrl = (doc: any): string => {
    const src = doc?.attributes ?? doc ?? {}
    return String(src?.Link ?? src?.URL ?? src?.Url ?? src?.url ?? src?.File?.url ?? src?.File?.data?.attributes?.url ?? '')
}

const getDocTokens = (doc: any): string => {
    const src = doc?.attributes ?? doc ?? {}
    const typeTokens = getDocTypeParts(doc).map((item: any) => `${String(item?.__component ?? '')} ${String(item?.Type ?? '')}`.toLowerCase()).join(' ')
    return `${typeTokens} ${String(src?.Category ?? '').toLowerCase()} ${getDocLabel(doc).toLowerCase()}`
}

const toViewDoc = (doc: any): ViewDoc => {
    const src = doc?.attributes ?? doc ?? {}
    const typePart: any = getDocTypeParts(doc)[0]
    return {
        id: doc?.id ?? src?.id ?? Math.random(),
        label: getDocLabel(doc),
        status: getDocStatus(doc),
        number: typePart?.Number ?? src?.Number ?? src?.RefID,
        year: typePart?.Year ?? src?.Year,
        type: typePart?.Type,
        category: src?.Category,
        expiry: src?.ExpiryDate,
        url: getDocUrl(doc),
        raw: doc
    }
}

const normalizeFetchedDocumentEntity = (payload: any): any | null => {
    const candidates = [payload?.response?.data, payload?.response?.response?.data, payload?.response, payload?.data, payload]
    for (const candidate of candidates) {
        const entity = unwrapEntity(candidate)
        if (entity == null) continue
        if (Array.isArray(entity)) {
            const first = entity.find((item) => item != null)
            const normalizedFirst = unwrapEntity(first)
            if (normalizedFirst?.id != null) return normalizedFirst
        }
        const src = entity?.attributes ?? entity
        if (entity?.id != null || src?.Link != null || src?.Type != null) return entity
    }
    return null
}

const getPersonName = (person: any, fallback: string): string => {
    const entity = unwrapEntity(person)
    if (entity == null) return fallback
    const contact = entity?.attributes?.ContactInfo ?? entity?.ContactInfo ?? {}
    const fullName = [contact?.FirstName ?? entity?.FirstName, contact?.FamilyName ?? entity?.FamilyName].filter(Boolean).join(' ').trim()
    return fullName || entity?.Name || entity?.FullName || fallback
}

const getContact = (person: any): { email: string, phone: string, country: string, dob: string, gender: string } => {
    const entity = unwrapEntity(person)
    const contact = entity?.attributes?.ContactInfo ?? entity?.ContactInfo ?? {}
    return {
        email: normalizeValue(contact?.Email ?? entity?.email),
        phone: normalizeValue(contact?.Phone ?? entity?.phone),
        country: normalizeValue(contact?.Country),
        dob: normalizeValue(contact?.DateOfBirth),
        gender: normalizeValue(contact?.Gender)
    }
}

const collectPersonDocRefs = (person: any, source?: any): any[] => {
    const entity = unwrapEntity(person)
    const sourceEntity = unwrapEntity(source)
    return [
        entity?.Documents,
        entity?.attributes?.Documents,
        sourceEntity?.Documents,
        sourceEntity?.attributes?.Documents,
        source?.Documents,
        source?.attributes?.Documents,
        source?.data?.attributes?.Documents
    ].flatMap((item) => unwrapListDeep<any>(item))
}

const uniqueDocs = (docs: any[]): any[] => {
    const map = new Map<string, any>()
    docs.forEach((doc, index) => {
        const id = toDocId(doc)
        const key = id != null ? `id-${id}` : `idx-${index}`
        if (!map.has(key)) map.set(key, doc)
    })
    return Array.from(map.values())
}

const getServiceIdentityId = (service: any): string => canonicalServiceId(
    service?.serviceId ??
    service?.ServiceID ??
    service?.ServiceId ??
    (typeof service?.id === 'string' ? service.id : undefined)
)
const getRelatedServiceId = (service: any): string => getServiceIdentityId(service)
const getServiceStatus = (service: any, fallback: string): string => String(service?.Status ?? service?.ServiceStatus ?? service?.Statuses?.[0] ?? fallback)
const isDevBuild = process.env.REACT_APP_IS_DEV === 'true'
const warnedUnknownRelatedServiceIds = new Set<string>()
const REPORT_READY_BY_SERVICE: Record<string, readonly string[]> = {
    property_rent_valuation: ['Report Ready', 'Completed'],
    unit_photography: ['PhotosReady'],
    request_inspection: ['Inspection Report Ready', 'Report Ready'],
    snagging_report: ['Checklist Ready For Review', 'Report Ready']
}
const APPOINTMENT_STATUSES = ['Scheduled', 'Inspection Scheduled', 'Photography Scheduled', 'Time Set']
const isStatusInList = (status: string, statuses: readonly string[]): boolean => statuses.some((item) => item.toLowerCase() === status.toLowerCase())
const getRelatedServiceStatusOptions = (serviceId: unknown): string[] => {
    const canonical = canonicalServiceId(serviceId)
    const options = getAdminAllowedStatuses(canonical)
    if (options.length === 0 && isDevBuild) {
        const warnKey = canonical || String(serviceId ?? '')
        if (!warnedUnknownRelatedServiceIds.has(warnKey)) {
            warnedUnknownRelatedServiceIds.add(warnKey)
            console.warn(`[UnitVerificationReviewPanel] Unknown related service id: ${String(serviceId)}`)
        }
    }
    return options
}

const DocActions = ({ url }: { url: string }): JSX.Element => {
    if (!url) return <span className="badge badge-light-danger">Missing link</span>
    return (
        <div className="d-inline-flex gap-2">
            <a href={url} target="_blank" rel="noreferrer" className="btn btn-sm btn-light-primary">Open</a>
            <a href={url} target="_blank" rel="noreferrer" download className="btn btn-sm btn-light">Download</a>
        </div>
    )
}

const DocInline = ({ doc, showDeedMeta = false }: { doc: ViewDoc, showDeedMeta?: boolean }): JSX.Element => (
    <div className="border border-gray-300 rounded p-2 mb-2 d-flex justify-content-between align-items-start gap-2">
        <div>
            <div className="fw-semibold text-dark fs-7">{doc.label}</div>
            <div className="d-flex flex-wrap gap-2 mt-1">
                <span className="badge badge-light">Status: {doc.status}</span>
                {doc.expiry && <span className="badge badge-light">Expiry: {doc.expiry}</span>}
                {doc.type && <span className="badge badge-light">Type: {doc.type}</span>}
                {doc.category && <span className="badge badge-light">Category: {doc.category}</span>}
                {showDeedMeta && doc.number && <span className="badge badge-light">Number: {doc.number}</span>}
                {showDeedMeta && doc.year && <span className="badge badge-light">Year: {doc.year}</span>}
            </div>
        </div>
        <DocActions url={doc.url} />
    </div>
)

const Field = ({ label, value, fullWidth = false }: { label: string, value: unknown, fullWidth?: boolean }): JSX.Element => (
    <div className={fullWidth ? 'col-12' : 'col-12 col-md-6'}>
        <div className="border border-gray-300 rounded p-2 h-100">
            <div className="text-gray-600 fs-8 text-uppercase">{label}</div>
            <div className="fw-semibold text-dark fs-7">{normalizeValue(value)}</div>
        </div>
    </div>
)

const toDraftNumber = (value: unknown): number | '' => {
    if (typeof value === 'number' && Number.isFinite(value)) return value
    if (typeof value === 'string' && value.trim().length > 0) {
        const parsed = Number(value)
        if (Number.isFinite(parsed)) return parsed
    }
    return ''
}

const createEmptyNocDraft = (): NocPreparedPayload => ({
    title: '',
    signers: [{ name: '', email: '' }],
    contracts: [{
        type: 'FAT_NOC',
        ref_number: '',
        landlord_name: '',
        unit_location: '',
        unit_developer: '',
        unit_type: '',
        unit_number: '',
        plot_number: '',
        title_deed: '',
        unit_area: '',
        bedrooms: '',
        parking_lots: ''
    }],
    metadata: {
        platform_id: "BSO_CLUB"
    }
})

const normalizeNocDraft = (draft: any): NocPreparedPayload => {
    const signer = draft?.signers?.[0] ?? {}
    const contract = draft?.contracts?.[0] ?? {}
    const metadata = (typeof draft?.metadata === 'object' && draft?.metadata != null) ? draft.metadata : {}
    const signerName = String(signer?.name ?? '')
    const landlordNameRaw = String(contract?.landlord_name ?? '')
    const landlordName = landlordNameRaw.trim().length > 0 ? landlordNameRaw : signerName
    return {
        title: String(draft?.title ?? ''),
        signers: [{
            name: signerName,
            email: String(signer?.email ?? '')
        }],
        contracts: [{
            type: 'FAT_NOC',
            ref_number: String(contract?.ref_number ?? ''),
            landlord_name: landlordName,
            unit_location: String(contract?.unit_location ?? ''),
            unit_developer: String(contract?.unit_developer ?? ''),
            unit_type: String(contract?.unit_type ?? ''),
            unit_number: String(contract?.unit_number ?? ''),
            plot_number: String(contract?.plot_number ?? ''),
            title_deed: String(contract?.title_deed ?? ''),
            unit_area: toDraftNumber(contract?.unit_area),
            bedrooms: toDraftNumber(contract?.bedrooms),
            parking_lots: toDraftNumber(contract?.parking_lots)
        }],
        metadata
    }
}

const getNocDraftSignerSelection = (draft: NocPreparedPayload): string => String(draft?.metadata?.signerSelection ?? '').trim()

const replaceNocDraftSignerWithCanonicalLandlord = (draft: NocPreparedPayload, canonicalDraft: NocPreparedPayload): NocPreparedPayload => {
    const previousSigner = draft.signers?.[0] ?? { name: '', email: '' }
    const canonicalSigner = canonicalDraft.signers?.[0] ?? { name: '', email: '' }
    const contract = draft.contracts?.[0] ?? {}
    const landlordNameMatchesPreviousSigner = String(contract.landlord_name ?? '').trim() === String(previousSigner.name ?? '').trim()

    return {
        ...draft,
        signers: [{ ...previousSigner, name: String(canonicalSigner.name ?? ''), email: String(canonicalSigner.email ?? '') }],
        contracts: [{
            ...contract,
            ...(landlordNameMatchesPreviousSigner ? { landlord_name: String(canonicalSigner.name ?? '') } : {})
        }],
        metadata: { ...(draft.metadata ?? {}), signerSelection: 'automatic_landlord' }
    }
}

const clearUnverifiedNocDraftSigner = (draft: NocPreparedPayload): NocPreparedPayload => {
    const previousSigner = draft.signers?.[0] ?? { name: '', email: '' }
    const contract = draft.contracts?.[0] ?? {}
    const landlordNameMatchesPreviousSigner = String(contract.landlord_name ?? '').trim() === String(previousSigner.name ?? '').trim()

    return {
        ...draft,
        signers: [{ ...previousSigner, name: '', email: '' }],
        contracts: [{
            ...contract,
            ...(landlordNameMatchesPreviousSigner ? { landlord_name: '' } : {})
        }],
        metadata: { ...(draft.metadata ?? {}), signerSelection: 'unresolved' }
    }
}

export default function UnitVerificationReviewPanel({ currentUnit, mode, flowType = 'request', relatedOrders = [], onReadyChange }: IProps): JSX.Element {
    const dispatch = useAppDispatch()
    const documentsState = useAppSelector((state: RootState) => state.documents.documents)
    const [localFetchedDocs, setLocalFetchedDocs] = useState<Record<number, any>>({})
    const [statusDraft, setStatusDraft] = useState<Record<string, string>>({})
    const [scheduleDraft, setScheduleDraft] = useState<Record<string, string>>({})
    const [notifyDraft, setNotifyDraft] = useState<Record<string, boolean>>({})
    const [statusOverride, setStatusOverride] = useState<Record<string, string>>({})
    const [statusSaving, setStatusSaving] = useState<Record<string, boolean>>({})
    const [statusError, setStatusError] = useState<Record<string, string>>({})
    const [statusSuccess, setStatusSuccess] = useState<Record<string, string>>({})
    const [responsibilityChecks, setResponsibilityChecks] = useState<Record<string, ResponsibilityChecks>>({})
    const [nocPreparation, setNocPreparation] = useState<NocPreparationState>({
        isOpen: false,
        orderId: null,
        serviceKey: null,
        pairKey: null,
        draft: null,
        loading: false,
        submitLoading: false,
        error: '',
        fieldErrors: {}
    })

    const isPublishedFlow = flowType === 'published'

    const unit = currentUnit?.attributes as any
    const images = toArray<string>(unit?.Images as string[] | undefined)
    const isRegisteredUnit = toArray<string>(unit?.Statuses).includes('Registered')
    const landlordPhoneRaw = String(unit?.User?.phone ?? unit?.User?.data?.attributes?.phone ?? unit?.User?.data?.phone ?? '').trim()
    const landlordPhone = landlordPhoneRaw.length > 0 ? landlordPhoneRaw : 'Not set'
    const trakheesiLink = String(unit?.PermitLink ?? '').trim()
    const jurisdiction = resolveUnitJurisdiction(unit)
    const isDifcUnit = jurisdiction === 'DIFC'

    const docsById = useMemo(() => {
        const map = new Map<number, any>()
        documentsState.forEach((doc: any) => {
            if (doc?.id != null) map.set(Number(doc.id), doc)
        })
        Object.entries(localFetchedDocs).forEach(([id, doc]) => map.set(Number(id), doc))
        return map
    }, [documentsState, localFetchedDocs])

    const resolveDocEntity = (value: any): any => {
        const id = toDocId(value)
        if (id != null && docsById.has(id)) return docsById.get(id)
        const entity = unwrapEntity(value)
        if (entity == null) return null
        const src = entity?.attributes ?? entity
        const hasEmbedded = entity?.attributes != null || src?.Link != null || src?.Type != null || src?.Category != null || src?.Statuses != null || src?.File != null
        if (hasEmbedded) return entity
        if (id != null) return null
        return entity
    }

    const resolveDocList = (value: any): any[] => unwrapListDeep<any>(value).map(resolveDocEntity).filter(Boolean)
    const unitDocs = useMemo(() => resolveDocList(unit?.Documents), [unit?.Documents, docsById])
    const landlordRelations = useMemo(() => unwrapListDeep<any>(unit?.Landlords), [unit?.Landlords])

    const missingDocIds = useMemo(() => {
        const ids: number[] = []
        landlordRelations.forEach((relation: any) => {
            const poaId = toDocId(relation?.PowerOfAttorney)
            if (poaId != null) ids.push(poaId)
            const owner = unwrapEntity(relation?.Landlord)
            collectPersonDocRefs(owner, relation?.Landlord).forEach((ref) => {
                const id = toDocId(ref)
                if (id != null) ids.push(id)
            })
            unwrapListDeep<any>(relation?.Representatives).forEach((repRef: any) => {
                const rep = unwrapEntity(repRef)
                collectPersonDocRefs(rep, repRef).forEach((ref) => {
                    const id = toDocId(ref)
                    if (id != null) ids.push(id)
                })
            })
        })
        return Array.from(new Set(ids)).filter((id) => !docsById.has(id))
    }, [landlordRelations, docsById])

    useEffect(() => {
        let cancelled = false
        const jwt = window.localStorage.getItem(ACCESS_KEY) ?? ''
        const fetchDocumentById = async (id: number): Promise<any | null> => {
            try {
                const bodyObject = { method: 'GET', collection: 'documents', id, query: 'populate[Type]=*&populate[User][fields][0]=id&populate[Unit][fields][0]=id&populate[Offer][fields][0]=id&populate[Advertise][fields][0]=id' }
                const response = await fetch(`${urlAPI}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
                    body: JSON.stringify(bodyObject)
                })
                const dataJSON = await response.json()
                return (dataJSON?.success === true && dataJSON?.response?.error == null) ? normalizeFetchedDocumentEntity(dataJSON) : null
            } catch {
                return null
            }
        }
        const run = async (): Promise<void> => {
            if (missingDocIds.length === 0) return
            const fetched = await Promise.all(missingDocIds.map(async (id) => [id, await fetchDocumentById(id)] as const))
            if (cancelled) return
            const next: Record<number, any> = {}
            fetched.forEach(([id, doc]) => { if (doc != null) next[id] = doc })
            if (Object.keys(next).length > 0) setLocalFetchedDocs((prev) => ({ ...prev, ...next }))
        }
        void run()
        return () => { cancelled = true }
    }, [missingDocIds])

    const titleDeedDocs = useMemo(() => unitDocs.filter((doc) => hasAny(getDocTokens(doc), TITLE_DEED_KEYS)).map(toViewDoc), [unitDocs])
    const unitDocuments = useMemo(() => unitDocs.map(toViewDoc), [unitDocs])
    const difcListingNoc = useMemo(() => {
        const document = findDifcListingNoc(unitDocs)
        return document == null ? null : toViewDoc(document)
    }, [unitDocs])

    const relationSections = useMemo(() => {
        return landlordRelations.map((relation: any, index: number) => {
            const owner = unwrapEntity(relation?.Landlord)
            const ownerIdentityDocs = uniqueDocs(resolveDocList(collectPersonDocRefs(owner, relation?.Landlord))).filter((doc) => hasAny(getDocTokens(doc), [...PASSPORT_KEYS, ...EID_KEYS])).map(toViewDoc)
            const reps = unwrapListDeep<any>(relation?.Representatives).map((repRef: any, repIndex: number) => {
                const rep = unwrapEntity(repRef)
                const docs = uniqueDocs(resolveDocList(collectPersonDocRefs(rep, repRef))).filter((doc) => hasAny(getDocTokens(doc), [...PASSPORT_KEYS, ...EID_KEYS])).map(toViewDoc)
                return { name: getPersonName(rep, `Representative ${repIndex + 1}`), contact: getContact(rep), docs }
            })
            const poaRaw = resolveDocEntity(relation?.PowerOfAttorney)
            return {
                ownerName: getPersonName(owner, `Owner ${index + 1}`),
                ownerContact: getContact(owner),
                ownerIdentityDocs,
                reps,
                poaDoc: poaRaw != null ? toViewDoc(poaRaw) : null,
                poaMissingButLinked: toDocId(relation?.PowerOfAttorney) != null && poaRaw == null
            }
        })
    }, [landlordRelations, docsById])

    const visibleRelatedOrders = useMemo(() => {
        return relatedOrders.filter((order: any) => !isUnpaidOrder(order))
    }, [relatedOrders])

    const relatedServicesParentOrder = useMemo(() => {
        return resolveRelatedServicesParentOrder(relatedOrders)
    }, [relatedOrders])

    const findingTenantLinked = relatedServicesParentOrder != null

    const consultationOrders = useMemo(() => {
        return getConsultationOrdersForUnit(visibleRelatedOrders, currentUnit?.id)
    }, [visibleRelatedOrders, currentUnit?.id])

    const relatedServicesRows = useMemo((): RelatedOrderRow[] => {
        return relatedServicesParentOrder == null ? [] : [relatedServicesParentOrder].map((order: any, orderIndex: number) => {
            const orderId = Number(order?.id ?? orderIndex + 1)
            const orderStatuses = toArray<string>(order?.Statuses).join(', ') || normalizeValue(order?.Statuses)
            const rootServices = toArray<any>(order?.Service).map((service: any, serviceIndex: number) => {
                const list: RelatedServiceView[] = []
                const mainId = getServiceIdentityId(service)
                const mainKey = `${orderId}:root:${mainId}`
                const mainLabel = serviceLabel(mainId, service?.Package)
                const mainStatus = statusOverride[mainKey] ?? getServiceStatus(service, orderStatuses)
                return {
                    key: `${orderId}-${serviceIndex}-main-${mainId}`,
                    serviceId: mainId,
                    label: service?.Package != null && mainLabel !== 'Unknown service' ? `${mainLabel} (${String(service.Package)})` : mainLabel,
                    status: mainStatus,
                    additional: normalizeValue(service?.AdditionalServices),
                    isRelated: false
                }
            })
            const relatedServices = toArray<any>(order?.RelatedServices).map((related: any, relatedIndex: number) => {
                const relatedId = getRelatedServiceId(related)
                const relatedKey = `${orderId}:${relatedId}`
                const relatedDisplayName = String(related?.Name ?? related?.Package ?? related?.Type ?? '').trim()
                return {
                    key: `${orderId}-related-${relatedIndex}-${relatedId}`,
                    serviceId: relatedId,
                    label: relatedDisplayName.length > 0 ? relatedDisplayName : serviceLabel(relatedId, related?.id),
                    status: statusOverride[relatedKey] ?? getServiceStatus(related, orderStatuses),
                    additional: normalizeValue(related?.AdditionalServices),
                    isRelated: true,
                    scheduledAt: typeof related?.ScheduledAt === 'string' ? related.ScheduledAt : undefined,
                    notify: typeof related?.Notify === 'boolean' ? related.Notify : undefined,
                    documentId: String(related?.documentId ?? '').trim()
                }
            })
            return {
                id: order?.id ?? `related-order-${orderIndex}`,
                order,
                services: [...rootServices, ...relatedServices],
                statuses: orderStatuses,
                amount: normalizeValue(order?.Amount),
                invoiceStatus: normalizeValue(order?.InvoiceStatus ?? order?.PaymentStatus),
                invoiceRef: normalizeValue(order?.InvoiceRefNumber),
                legacyNestedWarning: hasLegacyNestedRelatedServices(order)
            }
        })
    }, [relatedServicesParentOrder, statusOverride])

    const relatedServiceStatusOptionsById = useMemo((): Record<string, string[]> => {
        const optionsById: Record<string, string[]> = {}
        relatedServicesRows.forEach((row) => {
            row.services.forEach((service) => {
                if (!service.isRelated) return
                if (optionsById[service.serviceId] != null) return
                optionsById[service.serviceId] = getRelatedServiceStatusOptions(service.serviceId)
            })
        })
        return optionsById
    }, [relatedServicesRows])

    const landlordWarnings = useMemo(() => {
        const rows: string[] = []
        const repExists = relationSections.some((row) => row.reps.length > 0)
        const poaExists = relationSections.some((row) => row.poaDoc != null)
        const identityExists = relationSections.some((row) => row.ownerIdentityDocs.length > 0 || row.reps.some((rep) => rep.docs.length > 0))
        if (titleDeedDocs.length === 0 && !unitDocs.some((doc) => hasAny(getDocTokens(doc), TENANCY_KEYS))) rows.push('Missing Title Deed / OQOOD')
        if (repExists && !poaExists) rows.push('Representative exists but POA is missing or unresolved')
        if (!identityExists) rows.push('Owner/Representative identity docs (Passport/Emirates ID) not found')
        if (relationSections.some((row) => row.poaMissingButLinked)) rows.push('POA relation exists but document could not be resolved')
        if (images.length === 0) rows.push('No photos uploaded yet')
        return rows
    }, [titleDeedDocs.length, unitDocs, relationSections, images.length])
    const agentWarnings = useMemo(() => {
        const rows: string[] = []
        if (isDifcUnit) {
            if (!hasPublishEndDate(unit)) rows.push('DIFC NOC expiry date is missing')
            if (difcListingNoc == null) rows.push('DIFC Listing NOC is missing')
        } else if (trakheesiLink.length === 0) rows.push('Trakheesi / DLD link is missing')
        if (images.length === 0) rows.push('No photos uploaded yet')
        return rows
    }, [trakheesiLink, images.length, isDifcUnit, unit, difcListingNoc])
    const warnings = mode === 'landlord' ? landlordWarnings : agentWarnings

    const [checks, setChecks] = useState({
        reviewedSnapshot: false,
        reviewedDocuments: false,
        reviewedReadiness: false,
        calledLandlord: false,
        detailsConfirmed: false
    })

    useEffect(() => {
        setChecks({
            reviewedSnapshot: false,
            reviewedDocuments: false,
            reviewedReadiness: false,
            calledLandlord: false,
            detailsConfirmed: false
        })
        setStatusDraft({})
        setScheduleDraft({})
        setNotifyDraft({})
        setStatusOverride({})
        setStatusSaving({})
        setStatusError({})
        setStatusSuccess({})
        setResponsibilityChecks({})
        setNocPreparation({
            isOpen: false,
            orderId: null,
            serviceKey: null,
            pairKey: null,
            draft: null,
            loading: false,
            submitLoading: false,
            error: '',
            fieldErrors: {}
        })
    }, [currentUnit?.id, mode, flowType])

    useEffect(() => {
        const isLandlordVerifyGate = mode === 'landlord' && isRegisteredUnit
        if (isPublishedFlow) {
            onReadyChange(true)
            return
        }
        if (isLandlordVerifyGate) {
            onReadyChange(Boolean(checks.calledLandlord) && Boolean(checks.detailsConfirmed))
            return
        }
        onReadyChange(Boolean(checks.reviewedSnapshot) && Boolean(checks.reviewedDocuments) && Boolean(checks.reviewedReadiness))
    }, [checks, onReadyChange, isPublishedFlow, mode, isRegisteredUnit])

    const getResponsibilityForService = (serviceKey: string): ResponsibilityChecks => responsibilityChecks[serviceKey] ?? buildDefaultResponsibilityChecks()
    const isResponsibilityConfirmed = (serviceKey: string): boolean => Object.values(getResponsibilityForService(serviceKey)).every(Boolean)
    const getNocDraftStorageKey = (orderId: number): string => `${NOC_DRAFT_STORAGE_PREFIX}:${orderId}:${String(currentUnit?.id ?? 'unknown')}:${NOC_RELATED_SERVICE_ID}`

    const readNocDraftFromStorage = (orderId: number): NocPreparedPayload | null => {
        try {
            const raw = window.localStorage.getItem(getNocDraftStorageKey(orderId))
            if (!raw) return null
            const parsed = JSON.parse(raw)
            return typeof parsed === 'object' && parsed != null ? normalizeNocDraft(parsed) : null
        } catch {
            return null
        }
    }

    const writeNocDraftToStorage = (orderId: number, draft: NocPreparedPayload): void => {
        try {
            window.localStorage.setItem(getNocDraftStorageKey(orderId), JSON.stringify(draft))
        } catch {
            // ignore local persistence issues
        }
    }

    const clearNocDraftFromStorage = (orderId: number): void => {
        try {
            window.localStorage.removeItem(getNocDraftStorageKey(orderId))
        } catch {
            // ignore local persistence issues
        }
    }

    const validateNocDraft = (draft: NocPreparedPayload | null): Record<string, string> => {
        const errors: Record<string, string> = {}
        if (draft == null) {
            errors.form = 'NOC draft is missing.'
            return errors
        }

        const signer = draft.signers?.[0] ?? {}
        const contract = draft.contracts?.[0] ?? {}

        if (String(draft.title ?? '').trim().length === 0) errors.title = 'Title is required.'
        if (String(signer?.name ?? '').trim().length === 0) errors.signerName = 'Signer name is required.'
        if (!EMAIL_REGEXP.test(String(signer?.email ?? '').trim().toLowerCase())) errors.signerEmail = 'Signer email is invalid.'
        if (String(contract?.type ?? '') !== 'FAT_NOC') errors.form = 'Contract type must be FAT_NOC.'
        if (!REF_NUMBER_REGEXP.test(String(contract?.ref_number ?? '').trim())) errors.ref_number = 'Ref number has invalid format.'

        const requiredFields = ['landlord_name', 'unit_location', 'unit_developer', 'unit_type', 'unit_number', 'plot_number', 'title_deed']
        requiredFields.forEach((field) => {
            if (String(contract?.[field as keyof typeof contract] ?? '').trim().length === 0) errors[field] = `${field} is required.`
        })

        const numericFields = ['unit_area', 'bedrooms', 'parking_lots']
        numericFields.forEach((field) => {
            const rawValue = contract?.[field as keyof typeof contract]
            const value = Number(rawValue)
            if (rawValue === '' || rawValue == null || !Number.isFinite(value)) errors[field] = `${field} must be a valid number.`
        })

        return errors
    }

    const openNocPreparation = async (orderId: number, service: RelatedServiceView): Promise<void> => {
        if (!service.isRelated || service.serviceId !== NOC_RELATED_SERVICE_ID) return
        const pairKey = `${orderId}:${service.serviceId}`
        const selected = statusDraft[service.key] ?? service.status
        if (selected !== NOC_SENT_FOR_SIGNATURE_STATUS) return
        if (isPublishedFlow && !isResponsibilityConfirmed(service.key)) return

        setNocPreparation({
            isOpen: true,
            orderId,
            serviceKey: service.key,
            pairKey,
            draft: createEmptyNocDraft(),
            loading: true,
            submitLoading: false,
            error: '',
            fieldErrors: {}
        })

        const preparedForResolution = await dispatch(prepareNocSentForSignatureDraft({
            orderId,
            unit: currentUnit ?? {}
        }))
        const canonicalDraft = preparedForResolution?.draft != null ? normalizeNocDraft(preparedForResolution.draft) : null
        const canonicalSigner: NocPreparedPayload['signers'][number] = canonicalDraft?.signers?.[0] ?? { name: '', email: '' }
        const hasCanonicalLandlord = EMAIL_REGEXP.test(String(canonicalSigner.email ?? '').trim().toLowerCase())
        const storedDraft = readNocDraftFromStorage(orderId)
        if (storedDraft != null) {
            let nextDraft = normalizeNocDraft(storedDraft)
            if (hasCanonicalLandlord && canonicalDraft != null) {
                nextDraft = replaceNocDraftSignerWithCanonicalLandlord(nextDraft, canonicalDraft)
            } else if (getNocDraftSignerSelection(nextDraft) !== 'manual') {
                // Legacy drafts do not record their origin, so clear them rather than risk reusing
                // a formerly auto-selected representative or current-account contact.
                nextDraft = clearUnverifiedNocDraftSigner(nextDraft)
            }

            writeNocDraftToStorage(orderId, nextDraft)
            setNocPreparation((prev) => ({ ...prev, loading: false, draft: nextDraft, error: preparedForResolution?.textNotification ?? '', fieldErrors: {} }))
            return
        }

        if (preparedForResolution?.isSuccessful === true && preparedForResolution?.draft != null) {
            const nextDraft = normalizeNocDraft(preparedForResolution.draft)
            writeNocDraftToStorage(orderId, nextDraft)
            setNocPreparation((prev) => ({ ...prev, loading: false, draft: nextDraft, error: preparedForResolution?.textNotification ?? '', fieldErrors: {} }))
        } else {
            const fallbackDraft = createEmptyNocDraft()
            writeNocDraftToStorage(orderId, fallbackDraft)
            setNocPreparation((prev) => ({ ...prev, loading: false, draft: fallbackDraft, error: preparedForResolution?.textNotification ?? 'Could not prefill NOC draft. Fill fields manually and submit.', fieldErrors: {} }))
        }
    }

    const updateNocDraftField = (path: string, value: any): void => {
        setNocPreparation((prev) => {
            if (prev.orderId == null || prev.draft == null) return prev
            const nextDraft: NocPreparedPayload = { ...prev.draft, signers: [...(prev.draft.signers ?? [])], contracts: [...(prev.draft.contracts ?? [])] }
            nextDraft.signers[0] = { ...(nextDraft.signers?.[0] ?? {}) }
            nextDraft.contracts[0] = { ...(nextDraft.contracts?.[0] ?? {}) }

            if (path === 'title') nextDraft.title = value
            else if (path === 'signerName') {
                nextDraft.signers[0].name = value
                nextDraft.contracts[0].landlord_name = value
            }
            else if (path === 'signerEmail') nextDraft.signers[0].email = value
            else (nextDraft.contracts[0] as Record<string, any>)[path] = value

            if (path === 'signerName' || path === 'signerEmail') {
                nextDraft.metadata = { ...(nextDraft.metadata ?? {}), signerSelection: 'manual' }
            }

            writeNocDraftToStorage(prev.orderId, nextDraft)
            const nextFieldErrors = { ...prev.fieldErrors }
            delete nextFieldErrors[path]
            if (path === 'signerName') delete nextFieldErrors.landlord_name
            return { ...prev, draft: nextDraft, error: '', fieldErrors: nextFieldErrors }
        })
    }

    const closeNocPreparation = (): void => {
        setNocPreparation({
            isOpen: false,
            orderId: null,
            serviceKey: null,
            pairKey: null,
            draft: null,
            loading: false,
            submitLoading: false,
            error: '',
            fieldErrors: {}
        })
    }

    const submitNocPreparation = async (): Promise<void> => {
        if (nocPreparation.orderId == null || nocPreparation.serviceKey == null || nocPreparation.pairKey == null) return
        const fieldErrors = validateNocDraft(nocPreparation.draft)
        if (Object.keys(fieldErrors).length > 0) {
            setNocPreparation((prev) => ({ ...prev, fieldErrors, error: '' }))
            return
        }

        setNocPreparation((prev) => ({ ...prev, submitLoading: true, error: '', fieldErrors: {} }))
        setStatusSaving((prev) => ({ ...prev, [nocPreparation.pairKey as string]: true }))
        setStatusError((prev) => ({ ...prev, [nocPreparation.serviceKey as string]: '' }))
        setStatusSuccess((prev) => ({ ...prev, [nocPreparation.serviceKey as string]: '' }))

        const result = await dispatch(triggerNocSentForSignature({
            orderId: nocPreparation.orderId,
            unit: currentUnit ?? {},
            preparedPayload: nocPreparation.draft ?? undefined
        }))

        if (result?.isSuccessful === true) {
            setStatusOverride((prev) => ({ ...prev, [nocPreparation.pairKey as string]: NOC_SENT_FOR_SIGNATURE_STATUS }))
            setStatusDraft((prev) => {
                const next = { ...prev }
                delete next[nocPreparation.serviceKey as string]
                return next
            })
            setResponsibilityChecks((prev) => {
                const next = { ...prev }
                delete next[nocPreparation.serviceKey as string]
                return next
            })
            setStatusSuccess((prev) => ({ ...prev, [nocPreparation.serviceKey as string]: 'BoldSign document created and NOC updated.' }))
            clearNocDraftFromStorage(nocPreparation.orderId)
            closeNocPreparation()
        } else {
            const err = result?.textNotification ?? 'Failed to create NOC signing document.'
            setStatusError((prev) => ({ ...prev, [nocPreparation.serviceKey as string]: err }))
            setNocPreparation((prev) => ({ ...prev, submitLoading: false, error: err }))
        }
        setStatusSaving((prev) => ({ ...prev, [nocPreparation.pairKey as string]: false }))
    }

    const notifyRelatedServiceStatusUpdate = async (orderId: number, serviceId: string, nextStatus: string): Promise<void> => {
        const managedBy = String(unit?.ManagedBy ?? '').trim()
        if (managedBy !== 'Landlord') return
        const userId = Number(unit?.User?.data?.id ?? unit?.User?.id ?? 0)
        if (!Number.isFinite(userId) || userId <= 0) return
        const now = toUTCWithOffset(getCurrentDubaiDate(), 1)

        await sendNotification(now, userId, orderId, 'user-unit-new-activity', 'user unit new activity')

        const canonicalId = canonicalServiceId(serviceId)
        const normalizedStatus = String(nextStatus ?? '').trim()
        if (isStatusInList(normalizedStatus, REPORT_READY_BY_SERVICE[canonicalId] ?? [])) {
            await sendNotification(now, userId, orderId, 'user-unit-new-service-report', 'user unit new service report')
        }
        if (isStatusInList(normalizedStatus, APPOINTMENT_STATUSES)) {
            await sendNotification(now, userId, orderId, 'user-unit-new-service-appointment', 'user unit new service appointment')
        }
    }

    const onSaveRelatedStatus = async (order: any, service: RelatedServiceView): Promise<void> => {
        if (!service.isRelated) return
        const orderId = Number(order?.id)
        const nextStatus = statusDraft[service.key] ?? service.status
        const allowedStatuses = getRelatedServiceStatusOptions(service.serviceId)
        if (!allowedStatuses.includes(nextStatus as any)) return
        if (!isAdminRelatedServiceEditable(service.serviceId)) return
        if (isPublishedFlow && !isResponsibilityConfirmed(service.key)) return
        const requiresSchedule = isAdminScheduledStatus(service.serviceId, nextStatus)
        const scheduleInput = scheduleDraft[service.key] ?? toDateTimeInputValue(service.scheduledAt)
        if (requiresSchedule && scheduleInput.trim().length === 0) {
            setStatusError((prev) => ({ ...prev, [service.key]: 'ScheduledAt is required for this status.' }))
            return
        }
        const pairKey = `${orderId}:${service.serviceId}`
        setStatusSaving((prev) => ({ ...prev, [pairKey]: true }))
        setStatusError((prev) => ({ ...prev, [service.key]: '' }))
        setStatusSuccess((prev) => ({ ...prev, [service.key]: '' }))
        const patch: OrderRelatedServicePatch = {
            Status: nextStatus,
            Notify: notifyDraft[service.key] ?? service.notify ?? false,
            updatedAt: new Date().toISOString()
        }
        if (requiresSchedule) patch.ScheduledAt = new Date(scheduleInput).toISOString()

        try {
            await updateOrderRelatedService({ order, serviceId: service.serviceId, patch })
            setStatusOverride((prev) => ({ ...prev, [pairKey]: nextStatus }))
            setStatusDraft((prev) => {
                const next = { ...prev }
                delete next[service.key]
                return next
            })
            setResponsibilityChecks((prev) => {
                const next = { ...prev }
                delete next[service.key]
                return next
            })
            setStatusSuccess((prev) => ({ ...prev, [service.key]: 'Status updated successfully.' }))
            try {
                await notifyRelatedServiceStatusUpdate(orderId, service.serviceId, String(nextStatus))
            } catch {
                // Keep save behavior stable even if notification request fails.
            }
        }
        catch (error: any) {
            setStatusError((prev) => ({ ...prev, [service.key]: error?.message ?? 'Failed to update related service status.' }))
        }
        setStatusSaving((prev) => ({ ...prev, [pairKey]: false }))
    }

    const onSaveConsultationStatus = async (order: any): Promise<void> => {
        const orderId = Number(order?.id)
        const key = `${orderId}:root:service_consultation_request`
        const currentStatus = statusOverride[key] ?? getRootOrderStatus(order)
        const nextStatus = statusDraft[key] ?? currentStatus
        const contract = getLandlordServiceAdminContract('service_consultation_request')
        if (!Number.isFinite(orderId) || contract?.canUpdateRootOrder !== true || !getAdminAllowedStatuses('service_consultation_request').includes(nextStatus)) return

        const patch: RootOrderServiceStatusPatch = {
            Statuses: ['Custom stage'],
            ServiceStatus: nextStatus,
            updatedAt: new Date().toISOString(),
        }
        try {
            const body = buildRootOrderServiceStatusUpdateBody({ order, patch })
            assertSafeRootOrderServiceStatusBody(body)
            setStatusSaving((prev) => ({ ...prev, [key]: true }))
            setStatusError((prev) => ({ ...prev, [key]: '' }))
            setStatusSuccess((prev) => ({ ...prev, [key]: '' }))
            await updateRootOrderServiceStatus({ order, patch })
            setStatusOverride((prev) => ({ ...prev, [key]: nextStatus }))
            setStatusDraft((prev) => {
                const next = { ...prev }
                delete next[key]
                return next
            })
            setStatusSuccess((prev) => ({ ...prev, [key]: 'Status updated successfully.' }))
        } catch (error: any) {
            setStatusError((prev) => ({ ...prev, [key]: error?.message ?? 'Failed to update consultation status.' }))
        } finally {
            setStatusSaving((prev) => ({ ...prev, [key]: false }))
        }
    }

    const onSendNocForSignature = async (orderId: number, service: RelatedServiceView): Promise<void> => {
        const hasExistingDocument = String(service.documentId ?? '').trim().length > 0
        const alreadySent = service.status === NOC_SENT_FOR_SIGNATURE_STATUS
        if (hasExistingDocument || alreadySent) return
        await openNocPreparation(orderId, service)
    }

    const nocFieldError = (field: string): string => nocPreparation.fieldErrors[field] ?? ''
    const nocInputClass = (field: string): string => `form-control form-control-sm${nocFieldError(field) ? ' is-invalid' : ''}`

    useEffect(() => {
        if (!nocPreparation.isOpen) return
        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => { document.body.style.overflow = previousOverflow }
    }, [nocPreparation.isOpen])

    return (
        <div className="fv-row mb-4 m-0 p-0 w-100">
            <div className="card border border-gray-300 shadow-sm">
                <div className="card-body p-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="fw-bold fs-3">{mode === 'landlord' ? 'Landlord Verification' : 'Agent Verification'}</div>
                        <div className="d-flex flex-wrap gap-2 justify-content-end">
                            {mode === 'landlord' && (
                                <span className={`badge ${findingTenantLinked ? 'badge-light-primary' : 'badge-light-secondary'}`}>
                                    {findingTenantLinked ? 'Finding Tenant Flow' : 'Direct Landlord Flow'}
                                </span>
                            )}
                            <span className={`badge ${mode === 'landlord' ? 'badge-light-warning' : 'badge-light-info'}`}>{mode === 'landlord' ? 'Detailed Review' : 'Quick Review'}</span>
                        </div>
                    </div>
                    {mode === 'landlord' && isRegisteredUnit && (
                        <div className="alert alert-warning py-2 px-3 fs-8 mb-3">
                            <strong>You must call the landlord before approving this unit</strong>
                            <div className="mt-1">Landlord phone: {landlordPhone}</div>
                        </div>
                    )}

                    <div className="mb-4">
                        <div className="fw-bold text-dark mb-2">1. Unit details</div>
                        <div className="row g-2">
                            <Field label="RefID" value={unit?.RefID} fullWidth />
                            <Field label="Number" value={unit?.Number} />
                            <Field label="Type" value={unit?.Type} />
                            <Field label="Category" value={unit?.Category} />
                            <Field label="SizeArea" value={unit?.SizeArea} />
                            <Field label="Bedrooms" value={unit?.Bedrooms} />
                            <Field label="Bathrooms" value={unit?.Bathrooms} />
                            <Field label="Fit" value={unit?.Fit} />
                            <Field label="View" value={unit?.View} />
                            <Field label="ParkingSlotsAmount" value={unit?.ParkingSlotsAmount} />
                            <Field label="ACCount" value={unit?.ACCount} />
                            <Field label="ManagedBy" value={unit?.ManagedBy} />
                            <Field label="Statuses" value={toArray(unit?.Statuses)} />
                            <Field label="Property" value={unit?.Property?.data?.attributes?.Name} />
                            <Field label="Property Jurisdiction" value={jurisdiction} />
                            <Field label="Area" value={unit?.Property?.data?.attributes?.Area ?? unit?.Property?.data?.attributes?.Community} />
                            <Field label="Finding Tenant" value={findingTenantLinked ? 'Yes' : 'No'} />
                            <Field label="Marketing Use" value={unit?.Marketing?.Use} />
                            <Field label="Marketing Price" value={unit?.Marketing?.Price} />
                            <Field label="Marketing Discount Price" value={unit?.Marketing?.DiscountedPrice ?? unit?.Marketing?.DiscountPrice} />
                            <Field label="Paid Amount" value={unit?.Marketing?.PaidAmount ?? unit?.Marketing?.AmountPaid} />
                            <Field label="Marketing Headline" value={unit?.Marketing?.Headline} />
                        </div>
                    </div>

                    {mode === 'agent' && (
                        <div className="mb-4">
                            {isDifcUnit ? <>
                                <div className="fw-bold text-dark mb-2">2. DIFC Review</div>
                                <div className="row g-2 mb-3">
                                    <div className="col-12 col-md-6">
                                        <div className="border border-gray-300 rounded p-2 h-100">
                                            <div className="text-gray-600 fs-8 text-uppercase">Existing portal listing URL</div>
                                            {trakheesiLink.length > 0
                                                ? <a href={trakheesiLink} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-light-primary mt-1">Open listing URL</a>
                                                : <div className="fw-semibold text-dark fs-7">Not provided</div>}
                                        </div>
                                    </div>
                                    <Field label="DIFC NOC expiry date" value={unit?.PublishEndDate} />
                                </div>
                                <div className="fw-semibold text-dark fs-7 mb-2">DIFC Listing NOC</div>
                                {difcListingNoc != null
                                    ? <DocInline doc={difcListingNoc} />
                                    : <div className="text-gray-600 fs-8 border border-gray-300 rounded p-2">DIFC Listing NOC not provided</div>}
                            </> : <>
                                <div className="fw-bold text-dark mb-2">2. Trakheesi / DLD Review</div>
                                <div className="text-gray-700 fs-7 mb-2">
                                    Check the Trakheesi link in DLD, verify that the listing/property details match, and approve the unit if everything is correct.
                                </div>
                                {trakheesiLink.length > 0
                                    ? <a href={trakheesiLink} target="_blank" rel="noreferrer" className="btn btn-sm btn-light-primary">Open Trakheesi / DLD link</a>
                                    : <span className="badge badge-light-danger">Missing Trakheesi / DLD link</span>}
                            </>}
                        </div>
                    )}

                    {mode === 'landlord' && <div className="mb-4">
                        <div className="fw-bold text-dark mb-2">2. Related Services</div>
                        {relatedServicesRows.length > 0
                            ? relatedServicesRows.map((row) => {
                                const orderId = Number(row.id)
                                return (
                                    <div key={String(row.id)} className="border border-gray-300 rounded p-2 mb-2">
                                        <div className="fw-semibold text-dark fs-7 mb-1">Attached services</div>
                                        {row.legacyNestedWarning && (
                                            <div className="alert alert-warning py-2 mb-2 fs-8">
                                                Legacy nested related services detected. Migration required.
                                            </div>
                                        )}
                                        <div className="d-flex flex-column gap-2">
                                            {row.services.length > 0 ? row.services.map((service) => {
                                                const pairKey = `${orderId}:${service.serviceId}`
                                                const selected = statusDraft[service.key] ?? service.status
                                                const saving = statusSaving[pairKey] === true
                                                const options = relatedServiceStatusOptionsById[service.serviceId] ?? []
                                                const statusOptions = options.includes(selected) ? options : [selected, ...options]
                                                const editable = service.isRelated && isAdminRelatedServiceEditable(service.serviceId) && !row.legacyNestedWarning
                                                const requiresSchedule = editable && isAdminScheduledStatus(service.serviceId, selected)
                                                const scheduleValue = scheduleDraft[service.key] ?? toDateTimeInputValue(service.scheduledAt)
                                                const notifyValue = notifyDraft[service.key] ?? service.notify ?? false
                                                const publishedGateConfirmed = isResponsibilityConfirmed(service.key)
                                                const saveBlockedByGate = isPublishedFlow && editable && !publishedGateConfirmed
                                                const isNocRelated = service.serviceId === NOC_RELATED_SERVICE_ID
                                                const nocAlreadyGenerated = isNocRelated && (String(service.documentId ?? '').trim().length > 0 || service.status === NOC_SENT_FOR_SIGNATURE_STATUS)
                                                const shouldShowNocAction = editable && isNocRelated && selected === NOC_SENT_FOR_SIGNATURE_STATUS && !nocAlreadyGenerated
                                                const hasStatusChange = selected !== service.status
                                                const hasNotifyChange = notifyValue !== (service.notify ?? false)
                                                const canSave = editable && (hasStatusChange || hasNotifyChange || (requiresSchedule && scheduleValue.trim().length > 0))
                                                return (
                                                    <div key={service.key} className="border border-gray-200 rounded p-2 bg-light d-flex flex-column gap-2">
                                                        <div className="d-flex flex-wrap align-items-center gap-2">
                                                            <span className="fw-semibold text-dark fs-7">{service.label}</span>
                                                            <span className="badge badge-light">Status: {service.status}</span>
                                                            {service.isRelated && <span className="badge badge-light-info">Related</span>}
                                                            {service.scheduledAt && <span className="badge badge-light">ScheduledAt: {service.scheduledAt}</span>}
                                                            {service.isRelated && !editable && <span className="badge badge-light-warning">Read-only</span>}
                                                        </div>
                                                        {service.additional !== 'Not set' && <div className="text-gray-600 fs-8">Additional: {service.additional}</div>}
                                                        {editable && (
                                                            <div className="d-flex flex-wrap gap-2 align-items-center">
                                                                <select className="form-select form-select-sm mw-180px" value={selected} onChange={(event) => {
                                                                    setStatusDraft((prev) => ({ ...prev, [service.key]: event.target.value }))
                                                                    if (isPublishedFlow) {
                                                                        setResponsibilityChecks((prev) => ({ ...prev, [service.key]: buildDefaultResponsibilityChecks() }))
                                                                    }
                                                                    setStatusSuccess((prev) => ({ ...prev, [service.key]: '' }))
                                                                }}>
                                                                    {statusOptions.map((status) => <option key={status} value={status}>{status}</option>)}
                                                                </select>
                                                                {requiresSchedule && (
                                                                    <input
                                                                        className="form-control form-control-sm mw-200px"
                                                                        type="datetime-local"
                                                                        value={scheduleValue}
                                                                        onChange={(event) => setScheduleDraft((prev) => ({ ...prev, [service.key]: event.target.value }))}
                                                                    />
                                                                )}
                                                                <label className="form-check form-check-custom form-check-solid">
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        checked={notifyValue}
                                                                        onChange={(event) => setNotifyDraft((prev) => ({ ...prev, [service.key]: event.target.checked }))}
                                                                    />
                                                                    <span className="form-check-label text-gray-700 fs-8">Notify</span>
                                                                </label>
                                                                <button
                                                                    className="btn btn-sm btn-light-primary"
                                                                    type="button"
                                                                    disabled={saving || !canSave || saveBlockedByGate || shouldShowNocAction}
                                                                    onClick={() => {
                                                                        if (!Number.isFinite(orderId)) return
                                                                        void onSaveRelatedStatus(row.order, service)
                                                                    }}
                                                                >{saving ? 'Saving...' : 'Save status'}</button>
                                                                {shouldShowNocAction && (
                                                                    <button
                                                                        className="btn btn-sm btn-primary"
                                                                        type="button"
                                                                        disabled={saving || saveBlockedByGate}
                                                                        onClick={() => {
                                                                            if (!Number.isFinite(orderId)) return
                                                                            void onSendNocForSignature(orderId, service)
                                                                        }}
                                                                    >{saving ? 'Please wait...' : 'Prepare NOC document'}</button>
                                                                )}
                                                                {statusError[service.key] && <span className="text-danger fs-8">{statusError[service.key]}</span>}
                                                                {statusSuccess[service.key] && <span className="text-success fs-8">{statusSuccess[service.key]}</span>}
                                                            </div>
                                                        )}
                                                        {shouldShowNocAction && (
                                                            <div className="text-gray-700 fs-8">
                                                                Open preparation form, review/edit FAT NOC fields, then submit to create BoldSign document.
                                                            </div>
                                                        )}
                                                        {!shouldShowNocAction && nocAlreadyGenerated && (
                                                            <div className="text-gray-700 fs-8">
                                                                NOC document is already generated for this related service.
                                                            </div>
                                                        )}
                                                        {isPublishedFlow && editable && (
                                                            <div className="border border-warning rounded p-2 bg-white">
                                                                <div className="text-dark fs-8 fw-semibold mb-1">Responsibility confirmation (required to save)</div>
                                                                <div className="d-flex flex-column gap-1">
                                                                    <label className="form-check form-check-custom form-check-solid">
                                                                        <input className="form-check-input" type="checkbox" checked={getResponsibilityForService(service.key).reviewedServiceContext} onChange={(event) => setResponsibilityChecks((prev) => ({ ...prev, [service.key]: { ...getResponsibilityForService(service.key), reviewedServiceContext: event.target.checked } }))} />
                                                                        <span className="form-check-label text-gray-700 fs-8">I reviewed this service context.</span>
                                                                    </label>
                                                                    <label className="form-check form-check-custom form-check-solid">
                                                                        <input className="form-check-input" type="checkbox" checked={getResponsibilityForService(service.key).confirmedNextStatus} onChange={(event) => setResponsibilityChecks((prev) => ({ ...prev, [service.key]: { ...getResponsibilityForService(service.key), confirmedNextStatus: event.target.checked } }))} />
                                                                        <span className="form-check-label text-gray-700 fs-8">I confirm the selected next status.</span>
                                                                    </label>
                                                                    <label className="form-check form-check-custom form-check-solid">
                                                                        <input className="form-check-input" type="checkbox" checked={getResponsibilityForService(service.key).acknowledgedLiveImpact} onChange={(event) => setResponsibilityChecks((prev) => ({ ...prev, [service.key]: { ...getResponsibilityForService(service.key), acknowledgedLiveImpact: event.target.checked } }))} />
                                                                        <span className="form-check-label text-gray-700 fs-8">I understand this updates live operational flow.</span>
                                                                    </label>
                                                                    <label className="form-check form-check-custom form-check-solid">
                                                                        <input className="form-check-input" type="checkbox" checked={getResponsibilityForService(service.key).acceptedResponsibility} onChange={(event) => setResponsibilityChecks((prev) => ({ ...prev, [service.key]: { ...getResponsibilityForService(service.key), acceptedResponsibility: event.target.checked } }))} />
                                                                        <span className="form-check-label text-gray-700 fs-8">I accept responsibility for this update.</span>
                                                                    </label>
                                                                </div>
                                                                {!publishedGateConfirmed && <div className="text-warning fs-8 mt-1">Complete all confirmations to enable Save status.</div>}
                                                            </div>
                                                        )}
                                                    </div>
                                                )
                                            }) : <div className="text-gray-600 fs-8">No related services found</div>}
                                        </div>
                                        <div className="d-flex flex-wrap gap-2 mt-2">
                                            <span className="badge badge-light">Order status: {row.statuses}</span>
                                            <span className="badge badge-light">Amount: {row.amount}</span>
                                            {row.invoiceStatus !== 'Not set' && <span className="badge badge-light">Payment: {row.invoiceStatus}</span>}
                                            {row.invoiceRef !== 'Not set' && <span className="badge badge-light">Invoice: {row.invoiceRef}</span>}
                                        </div>
                                    </div>
                                )
                            })
                            : <div className="text-gray-600 fs-8 border border-gray-300 rounded p-2">No related services found</div>}
                    </div>}

                    {mode === 'landlord' && consultationOrders.length > 0 && <div className="mb-4">
                        <div className="fw-bold text-dark mb-2">Consultation Requests</div>
                        {consultationOrders.map((order: any) => {
                            const orderId = Number(order?.id)
                            const key = `${orderId}:root:service_consultation_request`
                            const display = resolveConsultationDisplay(order)
                            const status = statusOverride[key] ?? getRootOrderStatus(order)
                            const selected = statusDraft[key] ?? status
                            const allowedStatuses = getAdminAllowedStatuses('service_consultation_request')
                            const statusOptions = allowedStatuses.includes(selected) ? allowedStatuses : [selected, ...allowedStatuses]
                            const editable = getLandlordServiceAdminContract('service_consultation_request')?.canUpdateRootOrder === true
                            return (
                                <div key={String(order?.id)} className="border border-gray-300 rounded p-3 mb-2">
                                    <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                                        <span className="fw-semibold text-dark fs-7">{display.title}</span>
                                        <span className="badge badge-light">Status: {status || 'Not set'}</span>
                                    </div>
                                    {display.context && <div className="text-gray-600 fs-8 mb-1">Context: {display.context}</div>}
                                    {display.comments && <div className="text-gray-700 fs-8 mb-2">{display.comments}</div>}
                                    {editable && <div className="d-flex flex-wrap align-items-center gap-2">
                                        <select className="form-select form-select-sm mw-180px" value={selected} onChange={(event) => {
                                            setStatusDraft((prev) => ({ ...prev, [key]: event.target.value }))
                                            setStatusSuccess((prev) => ({ ...prev, [key]: '' }))
                                        }}>
                                            {statusOptions.map((value) => <option key={value} value={value}>{value}</option>)}
                                        </select>
                                        <button className="btn btn-sm btn-light-primary" type="button" disabled={statusSaving[key] === true || selected === status} onClick={() => { void onSaveConsultationStatus(order) }}>
                                            {statusSaving[key] === true ? 'Saving...' : 'Save status'}
                                        </button>
                                        {statusError[key] && <span className="text-danger fs-8">{statusError[key]}</span>}
                                        {statusSuccess[key] && <span className="text-success fs-8">{statusSuccess[key]}</span>}
                                    </div>}
                                </div>
                            )
                        })}
                    </div>}

                    <div className="mb-4">
                        <div className="fw-bold text-dark mb-2">3. Unit Documents</div>
                        {mode === 'landlord' && titleDeedDocs.length === 0 && <div className="text-gray-600 fs-8 border border-gray-300 rounded p-3">Title Deed / OQOOD not found</div>}
                        <div className="mb-3">
                            {unitDocuments.length > 0 ? unitDocuments.map((doc) => (
                                <DocInline key={`unit-${String(doc.id)}`} doc={doc} showDeedMeta={hasAny(getDocTokens(doc.raw), TITLE_DEED_KEYS)} />
                            )) : <div className="text-gray-600 fs-8 border border-gray-300 rounded p-2">No unit documents found</div>}
                        </div>
                    </div>

                    {mode === 'landlord' && <div className="mb-4">
                        <div className="fw-bold text-dark mb-2">4. Owner / Representative</div>
                        {relationSections.length > 0 ? relationSections.map((relation, idx) => (
                            <div key={`relation-${idx}`} className="border border-gray-300 rounded p-3 mb-3">
                                <div className="fw-bold text-dark fs-7 mb-1">Owner</div>
                                <div className="text-dark fs-7 fw-semibold">{relation.ownerName}</div>
                                <div className="text-gray-700 fs-8">Email: {relation.ownerContact.email}</div>
                                <div className="text-gray-700 fs-8">Phone: {relation.ownerContact.phone}</div>
                                {(relation.ownerContact.country !== 'Not set' || relation.ownerContact.dob !== 'Not set' || relation.ownerContact.gender !== 'Not set') && <div className="text-gray-600 fs-8">Country: {relation.ownerContact.country} | DOB: {relation.ownerContact.dob} | Gender: {relation.ownerContact.gender}</div>}
                                <div className="text-dark fs-8 fw-semibold mt-2 mb-1">Owner documents</div>
                                {relation.ownerIdentityDocs.length > 0 ? relation.ownerIdentityDocs.map((doc: ViewDoc) => <DocInline key={`owner-${String(doc.id)}`} doc={doc} />) : <div className="text-gray-600 fs-8">No owner Passport / Emirates ID found</div>}

                                {relation.reps.length > 0 && (
                                    <div className="mt-3">
                                        <div className="fw-bold text-dark fs-7 mb-2">Representative(s)</div>
                                        {relation.reps.map((rep: any, repIdx: number) => (
                                            <div key={`rep-${idx}-${repIdx}`} className="border border-gray-200 rounded p-2 mb-2 bg-light">
                                                <div className="text-dark fs-7 fw-semibold">{rep.name}</div>
                                                <div className="text-gray-700 fs-8">Email: {rep.contact.email}</div>
                                                <div className="text-gray-700 fs-8">Phone: {rep.contact.phone}</div>
                                                {(rep.contact.country !== 'Not set' || rep.contact.dob !== 'Not set' || rep.contact.gender !== 'Not set') && <div className="text-gray-600 fs-8">Country: {rep.contact.country} | DOB: {rep.contact.dob} | Gender: {rep.contact.gender}</div>}
                                                <div className="text-dark fs-8 fw-semibold mt-2 mb-1">Representative documents</div>
                                                {rep.docs.length > 0 ? rep.docs.map((doc: ViewDoc) => <DocInline key={`repdoc-${String(doc.id)}-${repIdx}`} doc={doc} />) : <div className="text-gray-600 fs-8">No representative Passport / Emirates ID found</div>}
                                                <div className="text-dark fs-8 fw-semibold mt-2 mb-1">POA</div>
                                                {relation.poaDoc ? <><div className="text-gray-700 fs-8 mb-1">POA linked to owner {relation.ownerName}</div><DocInline doc={relation.poaDoc} /></> : <div className="text-gray-600 fs-8">POA not found</div>}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )) : <div className="text-gray-600 fs-8 border border-gray-300 rounded p-2">No owner/representative relations found</div>}
                    </div>}

                    <div className="mb-4">
                        <div className="fw-bold text-dark mb-2">5. Media</div>
                        <div className="fs-7 text-gray-700 mb-2">Images: {images.length}</div>
                        {images.length > 0
                            ? <div className="d-flex flex-wrap gap-2">{images.slice(0, 8).map((image: string, index: number) => (
                                <a key={`${image}-${index}`} href={image} target="_blank" rel="noreferrer" className="border border-gray-300 rounded overflow-hidden d-block" style={{ width: 80, height: 80 }}>
                                    <img src={image} alt={`unit-media-${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </a>
                            ))}</div>
                            : <div className="text-gray-600 fs-8 border border-gray-300 rounded p-3">No photos yet. Admin may contact client / suggest photography support</div>}
                    </div>

                    <div className="mb-4">
                        <div className="fw-bold text-dark mb-2">6. Warnings / Checklist</div>
                        {warnings.length > 0 ? warnings.map((warning) => <div key={warning} className="badge badge-light-danger text-start me-2 mb-2">{warning}</div>) : <span className="badge badge-light-success">No critical warnings</span>}
                    </div>

                    {nocPreparation.isOpen && createPortal((
                        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ background: 'rgba(0,0,0,0.55)', zIndex: 10050 }}>
                            <div className="bg-white rounded shadow p-4 w-100 mx-3" style={{ maxWidth: 980, maxHeight: '90vh', overflowY: 'auto' }}>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div className="fw-bold fs-4">NOC Preparation</div>
                                    <button className="btn btn-sm btn-light" type="button" onClick={closeNocPreparation}>Close</button>
                                </div>

                                {nocPreparation.loading && <div className="text-gray-700 fs-7">Preparing draft...</div>}
                                {!nocPreparation.loading && nocPreparation.draft != null && (
                                    <div className="d-flex flex-column gap-3">
                                        <div className="row g-2">
                                            <div className="col-12 col-md-6">
                                                <label className="form-label fs-8 text-gray-700">Signer Name *</label>
                                                <input
                                                    className={nocInputClass('signerName')}
                                                    value={String(nocPreparation.draft?.signers?.[0]?.name ?? '')}
                                                    onChange={(event) => updateNocDraftField('signerName', event.target.value)}
                                                />
                                                {nocFieldError('signerName') && <div className="invalid-feedback d-block fs-8">{nocFieldError('signerName')}</div>}
                                            </div>
                                            <div className="col-12 col-md-6">
                                                <label className="form-label fs-8 text-gray-700">Signer Email *</label>
                                                <input
                                                    className={nocInputClass('signerEmail')}
                                                    value={String(nocPreparation.draft?.signers?.[0]?.email ?? '')}
                                                    onChange={(event) => updateNocDraftField('signerEmail', event.target.value)}
                                                />
                                                {nocFieldError('signerEmail') && <div className="invalid-feedback d-block fs-8">{nocFieldError('signerEmail')}</div>}
                                            </div>
                                            <div className="col-12">
                                                <label className="form-label fs-8 text-gray-700">Title *</label>
                                                <input
                                                    className={nocInputClass('title')}
                                                    value={String(nocPreparation.draft?.title ?? '')}
                                                    onChange={(event) => updateNocDraftField('title', event.target.value)}
                                                />
                                                {nocFieldError('title') && <div className="invalid-feedback d-block fs-8">{nocFieldError('title')}</div>}
                                            </div>
                                        </div>

                                        <div className="row g-2">
                                            <div className="col-12 col-md-4">
                                                <label className="form-label fs-8 text-gray-700">ref_number *</label>
                                                <input className={nocInputClass('ref_number')} value={String(nocPreparation.draft?.contracts?.[0]?.ref_number ?? '')} onChange={(event) => updateNocDraftField('ref_number', event.target.value)} />
                                                {nocFieldError('ref_number') && <div className="invalid-feedback d-block fs-8">{nocFieldError('ref_number')}</div>}
                                            </div>
                                            <div className="col-12 col-md-8">
                                                <label className="form-label fs-8 text-gray-700">landlord_name *</label>
                                                <input className={nocInputClass('landlord_name')} value={String(nocPreparation.draft?.contracts?.[0]?.landlord_name ?? '')} onChange={(event) => updateNocDraftField('landlord_name', event.target.value)} />
                                                {nocFieldError('landlord_name') && <div className="invalid-feedback d-block fs-8">{nocFieldError('landlord_name')}</div>}
                                            </div>
                                            <div className="col-12">
                                                <label className="form-label fs-8 text-gray-700">unit_location *</label>
                                                <input className={nocInputClass('unit_location')} value={String(nocPreparation.draft?.contracts?.[0]?.unit_location ?? '')} onChange={(event) => updateNocDraftField('unit_location', event.target.value)} />
                                                {nocFieldError('unit_location') && <div className="invalid-feedback d-block fs-8">{nocFieldError('unit_location')}</div>}
                                            </div>
                                            <div className="col-12 col-md-6">
                                                <label className="form-label fs-8 text-gray-700">unit_developer *</label>
                                                <input className={nocInputClass('unit_developer')} value={String(nocPreparation.draft?.contracts?.[0]?.unit_developer ?? '')} onChange={(event) => updateNocDraftField('unit_developer', event.target.value)} />
                                                {nocFieldError('unit_developer') && <div className="invalid-feedback d-block fs-8">{nocFieldError('unit_developer')}</div>}
                                            </div>
                                            <div className="col-12 col-md-6">
                                                <label className="form-label fs-8 text-gray-700">unit_type *</label>
                                                <input className={nocInputClass('unit_type')} value={String(nocPreparation.draft?.contracts?.[0]?.unit_type ?? '')} onChange={(event) => updateNocDraftField('unit_type', event.target.value)} />
                                                {nocFieldError('unit_type') && <div className="invalid-feedback d-block fs-8">{nocFieldError('unit_type')}</div>}
                                            </div>
                                            <div className="col-12 col-md-4">
                                                <label className="form-label fs-8 text-gray-700">unit_number *</label>
                                                <input className={nocInputClass('unit_number')} value={String(nocPreparation.draft?.contracts?.[0]?.unit_number ?? '')} onChange={(event) => updateNocDraftField('unit_number', event.target.value)} />
                                                {nocFieldError('unit_number') && <div className="invalid-feedback d-block fs-8">{nocFieldError('unit_number')}</div>}
                                            </div>
                                            <div className="col-12 col-md-4">
                                                <label className="form-label fs-8 text-gray-700">plot_number *</label>
                                                <input className={nocInputClass('plot_number')} value={String(nocPreparation.draft?.contracts?.[0]?.plot_number ?? '')} onChange={(event) => updateNocDraftField('plot_number', event.target.value)} />
                                                {nocFieldError('plot_number') && <div className="invalid-feedback d-block fs-8">{nocFieldError('plot_number')}</div>}
                                            </div>
                                            <div className="col-12 col-md-4">
                                                <label className="form-label fs-8 text-gray-700">title_deed *</label>
                                                <input className={nocInputClass('title_deed')} value={String(nocPreparation.draft?.contracts?.[0]?.title_deed ?? '')} onChange={(event) => updateNocDraftField('title_deed', event.target.value)} />
                                                {nocFieldError('title_deed') && <div className="invalid-feedback d-block fs-8">{nocFieldError('title_deed')}</div>}
                                            </div>
                                            <div className="col-12 col-md-4">
                                                <label className="form-label fs-8 text-gray-700">unit_area *</label>
                                                <input className={nocInputClass('unit_area')} type="number" value={String(nocPreparation.draft?.contracts?.[0]?.unit_area ?? '')} onChange={(event) => updateNocDraftField('unit_area', event.target.value === '' ? '' : Number(event.target.value))} />
                                                {nocFieldError('unit_area') && <div className="invalid-feedback d-block fs-8">{nocFieldError('unit_area')}</div>}
                                            </div>
                                            <div className="col-12 col-md-4">
                                                <label className="form-label fs-8 text-gray-700">bedrooms *</label>
                                                <input className={nocInputClass('bedrooms')} type="number" value={String(nocPreparation.draft?.contracts?.[0]?.bedrooms ?? '')} onChange={(event) => updateNocDraftField('bedrooms', event.target.value === '' ? '' : Number(event.target.value))} />
                                                {nocFieldError('bedrooms') && <div className="invalid-feedback d-block fs-8">{nocFieldError('bedrooms')}</div>}
                                            </div>
                                            <div className="col-12 col-md-4">
                                                <label className="form-label fs-8 text-gray-700">parking_lots *</label>
                                                <input className={nocInputClass('parking_lots')} type="number" value={String(nocPreparation.draft?.contracts?.[0]?.parking_lots ?? '')} onChange={(event) => updateNocDraftField('parking_lots', event.target.value === '' ? '' : Number(event.target.value))} />
                                                {nocFieldError('parking_lots') && <div className="invalid-feedback d-block fs-8">{nocFieldError('parking_lots')}</div>}
                                            </div>
                                        </div>

                                        {nocFieldError('form') && <div className="text-danger fs-7">{nocFieldError('form')}</div>}
                                        {nocPreparation.error && <div className="text-danger fs-7">{nocPreparation.error}</div>}

                                        <div className="d-flex justify-content-end gap-2">
                                            <button className="btn btn-sm btn-light" type="button" onClick={closeNocPreparation} disabled={nocPreparation.submitLoading}>Close</button>
                                            <button className="btn btn-sm btn-primary" type="button" onClick={() => { void submitNocPreparation() }} disabled={nocPreparation.submitLoading || nocPreparation.loading}>
                                                {nocPreparation.submitLoading ? 'Submitting...' : 'Submit & Create BoldSign Document'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ), document.body)}

                    {!isPublishedFlow && (
                        <>
                            <div className="separator my-3"></div>
                            <div className="d-flex flex-column gap-2">
                                {mode === 'landlord' && isRegisteredUnit ? (
                                    <>
                                        <label className="form-check form-check-custom form-check-solid">
                                            <input className="form-check-input" type="checkbox" checked={checks.calledLandlord} onChange={(event) => setChecks((prev) => ({ ...prev, calledLandlord: event.target.checked }))} />
                                            <span className="form-check-label text-gray-700">I have called the landlord</span>
                                        </label>
                                        <label className="form-check form-check-custom form-check-solid">
                                            <input className="form-check-input" type="checkbox" checked={checks.detailsConfirmed} onChange={(event) => setChecks((prev) => ({ ...prev, detailsConfirmed: event.target.checked }))} />
                                            <span className="form-check-label text-gray-700">All details are confirmed</span>
                                        </label>
                                    </>
                                ) : mode === 'agent' ? (
                                    <>
                                        <label className="form-check form-check-custom form-check-solid">
                                            <input className="form-check-input" type="checkbox" checked={checks.reviewedSnapshot} onChange={(event) => setChecks((prev) => ({ ...prev, reviewedSnapshot: event.target.checked }))} />
                                            <span className="form-check-label text-gray-700">I reviewed the unit snapshot</span>
                                        </label>
                                        <label className="form-check form-check-custom form-check-solid">
                                            <input className="form-check-input" type="checkbox" checked={checks.reviewedDocuments} onChange={(event) => setChecks((prev) => ({ ...prev, reviewedDocuments: event.target.checked }))} />
                                            <span className="form-check-label text-gray-700">{isDifcUnit ? 'I checked the DIFC listing URL and NOC' : 'I checked the Trakheesi link in DLD'}</span>
                                        </label>
                                        <label className="form-check form-check-custom form-check-solid">
                                            <input className="form-check-input" type="checkbox" checked={checks.reviewedReadiness} onChange={(event) => setChecks((prev) => ({ ...prev, reviewedReadiness: event.target.checked }))} />
                                            <span className="form-check-label text-gray-700">I verified the listing details and readiness</span>
                                        </label>
                                    </>
                                ) : (
                                    <>
                                        <label className="form-check form-check-custom form-check-solid">
                                            <input className="form-check-input" type="checkbox" checked={checks.reviewedSnapshot} onChange={(event) => setChecks((prev) => ({ ...prev, reviewedSnapshot: event.target.checked }))} />
                                            <span className="form-check-label text-gray-700">I reviewed snapshot</span>
                                        </label>
                                        <label className="form-check form-check-custom form-check-solid">
                                            <input className="form-check-input" type="checkbox" checked={checks.reviewedDocuments} onChange={(event) => setChecks((prev) => ({ ...prev, reviewedDocuments: event.target.checked }))} />
                                            <span className="form-check-label text-gray-700">I reviewed documents and links</span>
                                        </label>
                                        <label className="form-check form-check-custom form-check-solid">
                                            <input className="form-check-input" type="checkbox" checked={checks.reviewedReadiness} onChange={(event) => setChecks((prev) => ({ ...prev, reviewedReadiness: event.target.checked }))} />
                                            <span className="form-check-label text-gray-700">I reviewed warnings/readiness</span>
                                        </label>
                                    </>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}
